import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Helmet } from "react-helmet-async";
import PageBanner from "@/components/PageBanner";
import PaymentPartners from "@/components/PaymentPartners";
import { useLanguage } from "@/lib/LanguageContext";

interface AccommodationProps {
  name: string;
  location: string;
  description: string;
  priceRange: {
    peak: string;
    offPeak: string;
  };
  amenities: string[];
  website?: string;
}

const MurchisonFallsAccommodations: AccommodationProps[] = [
  {
    name: "Sambiya River Lodge",
    location: "Situated on the south bank of the Nile River",
    description: "Comfortable rooms and suites with stunning views of the Nile",
    priceRange: {
      peak: "$150",
      offPeak: "$100",
    },
    amenities: ["Restaurant", "Bar", "Swimming pool", "Laundry services"],
    website: "https://sambiyariverlodge.com",
  },
  {
    name: "Paraa Safari Lodge",
    location: "Occupies a prime position on the north bank of the Nile River",
    description: "54 rooms with stunning views of the Nile",
    priceRange: {
      peak: "$200",
      offPeak: "$150",
    },
    amenities: ["Restaurant", "Bar", "Swimming pool", "Laundry services"],
    website: "https://paraalodge.com",
  },
  {
    name: "Chobe Safari Lodge",
    location: "Located on the south bank of the Nile River",
    description: "Luxurious rooms and suites with stunning views of the Nile",
    priceRange: {
      peak: "$250",
      offPeak: "$200",
    },
    amenities: ["Restaurant", "Bar", "Swimming pool", "Laundry services"],
    website: "https://chobelodgeuganda.com",
  },
];

const BwindiAccommodations: AccommodationProps[] = [
  {
    name: "Gorilla Leisure Lodge",
    location: "Located near the park entrance",
    description:
      "Comfortable rooms and suites with stunning views of the surrounding forest",
    priceRange: {
      peak: "$180",
      offPeak: "$120",
    },
    amenities: ["Restaurant", "Bar", "Laundry services"],
    website: "https://gorillaleisurelodge.com",
  },
  {
    name: "Bakiga Lodge",
    location: "Situated near the park entrance",
    description: "Cozy rooms and cottages with private balconies",
    priceRange: {
      peak: "$150",
      offPeak: "$100",
    },
    amenities: ["Restaurant", "Bar", "Laundry services"],
    website: "https://bakigalodge.com",
  },
  {
    name: "Silverback Lodge",
    location: "Located near the park entrance",
    description:
      "Comfortable rooms and suites with stunning views of the surrounding forest",
    priceRange: {
      peak: "$200",
      offPeak: "$150",
    },
    amenities: ["Restaurant", "Bar", "Laundry services"],
    website: "https://silverbacklodge.com",
  },
];

const QueenElizabethAccommodations: AccommodationProps[] = [
  {
    name: "Mweya Safari Lodge",
    location: "Situated on a peninsula overlooking Lake Edward",
    description: "Luxurious rooms and suites with stunning views",
    priceRange: {
      peak: "$350",
      offPeak: "$200",
    },
    amenities: ["Restaurant", "Bar", "Swimming pool", "Laundry services"],
    website: "https://mweyalodge.com",
  },
  {
    name: "Parkview Safari Lodge",
    location: "Situated on a hill overlooking the Kasese district",
    description: "Comfortable rooms and suites with stunning views",
    priceRange: {
      peak: "$250",
      offPeak: "$100",
    },
    amenities: ["Restaurant", "Bar", "Laundry services"],
    website: "https://parkviewsafarilodge.com",
  },
];

const KampalaAccommodations: AccommodationProps[] = [
  {
    name: "Rado Residence - Air BnB",
    location: "Located in a quiet Kampala neighborhood with easy access to the city center",
    description: "Modern and comfortable apartments with a home-away-from-home atmosphere",
    priceRange: {
      peak: "$80",
      offPeak: "$60",
    },
    amenities: ["Free Wi-Fi", "Fully equipped kitchen", "Air conditioning", "Cable TV", "24/7 security", "Laundry services"],
    website: "https://airbnb.com/h/rado-residence",
  },
];

const AccommodationSection = ({
  title,
  accommodations,
}: {
  title: string;
  accommodations: AccommodationProps[];
}) => (
  <section className="mb-16">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="text-center mb-8"
    >
      <h2 className="text-3xl font-bold mb-2 font-heading">{title}</h2>
      <div className="w-24 h-1 bg-green-600 mx-auto rounded-full mb-4"></div>
    </motion.div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {accommodations.map((accommodation, index) => (
        <motion.div
          key={accommodation.name}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.1 }}
        >
          <Card className="h-full flex flex-col bg-white">
            <CardHeader>
              <CardTitle className="font-heading">{accommodation.name}</CardTitle>
              <CardDescription>{accommodation.location}</CardDescription>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              <p className="text-muted-foreground mb-4 text-base">
                {accommodation.description.split(' ').map((word, i) => 
                  i === 0 ? <span key={i} className="font-semibold">{word} </span> : word + ' '
                )}
              </p>
              <div className="mb-4">
                <p className="text-sm font-medium mb-1">Price Range:</p>
                <p className="text-sm text-muted-foreground">
                  Peak Season: {accommodation.priceRange.peak} per night
                </p>
                <p className="text-sm text-muted-foreground">
                  Off-Peak: {accommodation.priceRange.offPeak} per night
                </p>
              </div>
              <div className="mb-6">
                <p className="text-sm font-medium mb-1">Amenities:</p>
                <ul className="text-sm text-muted-foreground list-disc list-inside">
                  {accommodation.amenities.map((amenity) => (
                    <li key={amenity}>{amenity}</li>
                  ))}
                </ul>
              </div>
              {accommodation.website && (
                <Button
                  className="mt-auto"
                  onClick={() => window.open(accommodation.website, "_blank")}
                >
                  Visit Website
                </Button>
              )}
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </div>
  </section>
);

export default function Accommodation() {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{t("accommodation")} | Ecoplace Tours</title>
        <meta
          name="description"
          content="Discover our carefully selected accommodation options across Uganda's most beautiful national parks"
        />
      </Helmet>

      <PageBanner
        title={t("accommodation")}
        description="Experience comfort and luxury in the heart of Uganda's wilderness. From luxury lodges to tented camps, find your perfect stay."
        backgroundImage="/chobesafarilodge.jpg"
      />

       <div className="container mx-auto px-4 py-8 bg-white">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12 text-center"
        >
          <p className="text-xl md:text-2xl leading-relaxed max-w-4xl mx-auto text-gray-700">
            <span className="font-semibold text-green-700">Welcome to our handpicked accommodations</span> across East Africa's most spectacular locations. We've carefully selected lodges, camps, and hotels that combine comfort with authentic experiences, ensuring your stay is as memorable as your adventures.
          </p>
        </motion.div>

        <AccommodationSection
          title="Murchison Falls National Park"
          accommodations={MurchisonFallsAccommodations}
        />

        <AccommodationSection
          title="Bwindi Impenetrable National Park"
          accommodations={BwindiAccommodations}
        />

        <AccommodationSection
          title="Queen Elizabeth National Park"
          accommodations={QueenElizabethAccommodations}
        />

        <AccommodationSection
          title="Kampala City Accommodations"
          accommodations={KampalaAccommodations}
        />
      </div>
      
      <PaymentPartners />
    </div>
  );
}